/**
 * Le champ d'autocomplétion de `<SelectField presentation="inline">` : on tape dans le champ ou on le touche, et sa
 * liste s'ouvre sous lui, dans la page — jamais une feuille ni une boîte par-dessus (le live du masteur en a déjà
 * une, « Plus d'options »). La liste est `<Autocomplete variant="menu">`, posée par `<Overlay placement="anchor">`.
 *
 * Fermé, le champ montre le choix en cours, son icône devant. Ouvert, ce qu'on tape filtre la liste (le choix en
 * cours reste lisible en indication) ; une liste qu'on ne cherche pas (trois durées) s'ouvre sans clavier. Au web :
 * flèches haut et bas, Entrée pour choisir, Échap pour refermer, Tab pour quitter. Choix unique.
 */
import { useEffect, useMemo, useRef, useState } from "react"
import { Platform, Pressable, View } from "react-native"
import type { TextInput as RNTextInput } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { Overlay, type OverlayAnchor } from "../../atoms/Overlay"
import { ScrollView } from "../../atoms/ScrollView"
import { TextInput, TEXT_INPUT_HEIGHT } from "../../atoms/TextInput/TextInput"
import { Text } from "../../atoms/typography/Text"
import {
    Autocomplete,
    AutocompleteOptionIcon,
    groupAutocompleteItems,
    orderedAutocompleteItems,
} from "../Autocomplete/Autocomplete"
import { Card } from "../Card"
import { type SelectFieldProps } from "./SelectField"

type WebKeyEvent = { key?: string; preventDefault: () => void; stopPropagation: () => void }
type WebDocument = {
    addEventListener: (type: string, listener: (event: WebKeyEvent) => void, capture?: boolean) => void
    removeEventListener: (type: string, listener: (event: WebKeyEvent) => void, capture?: boolean) => void
}

export function SelectFieldInline<T>(props: SelectFieldProps<T> & { withSearch: boolean }) {
    const {
        label,
        placeholder = "Choisir",
        items,
        getItemId,
        getItemLabel,
        getItemIcon,
        selectedIds,
        onChange,
        searchPlaceholder,
        withSearch,
        groupBy,
        noResultAction,
        loading = false,
        error,
        disabled = false,
        defaultOpen = false,
        testID,
    } = props

    const [open, setOpen] = useState(defaultOpen)
    const [query, setQuery] = useState("")
    const [highlight, setHighlight] = useState<string | number | null>(null)
    const [anchor, setAnchor] = useState<OverlayAnchor | null>(null)
    const boxRef = useRef<View>(null)
    const inputRef = useRef<RNTextInput>(null)

    const selectedKey = selectedIds.length > 0 ? String(selectedIds[0]) : null
    const selected = selectedKey != null ? items.find((item) => String(getItemId(item)) === selectedKey) : undefined
    const summary = selected != null ? getItemLabel(selected) : null
    const selectedIcon = selected != null ? (getItemIcon?.(selected) ?? null) : null
    const typed = withSearch ? query : ""

    const order = useMemo(
        () => orderedAutocompleteItems(groupAutocompleteItems({ items, query: typed, getItemLabel, groupBy })),
        [items, typed, getItemLabel, groupBy]
    )
    // L'option éclairée : celle des flèches tant qu'elle est à l'écran, sinon le choix en cours, sinon la première.
    const lit =
        [highlight, selected != null ? getItemId(selected) : null].find(
            (id) => id != null && order.some((item) => String(getItemId(item)) === String(id))
        ) ?? (order[0] != null ? getItemId(order[0]) : null)

    // La boîte du champ, mesurée dans la fenêtre : la liste se pose sous elle (sans le message d'erreur dessous).
    const measure = () =>
        boxRef.current?.measureInWindow((x, y, width) => setAnchor({ x, y, width, height: TEXT_INPUT_HEIGHT }))

    const openList = (typed = "") => {
        if (disabled || open) return
        setQuery(typed)
        setHighlight(null)
        setAnchor(null)
        measure()
        setOpen(true)
    }
    const close = () => {
        setOpen(false)
        setQuery("")
        // Sur un téléphone, le champ refermé rend le clavier : le toucher de nouveau rouvre la liste.
        if (Platform.OS !== "web") inputRef.current?.blur()
    }
    const pick = (id: string | number, fromKeyboard: boolean) => {
        // Rechoisir l'option en cours la passe aussi à l'appelant (le chapitre rechoisi se retire), comme la feuille.
        onChange([id])
        close()
        // Choisi au toucher ou à la souris, le champ rend la main (le clavier du téléphone se range) ; à Entrée, il
        // garde le curseur, pour passer au champ suivant au clavier.
        if (!fromKeyboard) inputRef.current?.blur()
    }

    // Le champ bouge quand le clavier du téléphone s'ouvre (la feuille qui le porte remonte) : la liste le suit.
    useEffect(() => {
        if (!open || Platform.OS === "web") return
        const timer = setTimeout(measure, 300)
        return () => clearTimeout(timer)
    }, [open])

    const menuNoResult =
        noResultAction != null
            ? {
                  ...noResultAction,
                  onPress: (q: string) => {
                      noResultAction.onPress(q)
                      close()
                  },
              }
            : undefined

    // Le clavier lit le dernier rendu : la liste filtrée, l'option éclairée, et le `onChange` à jour de l'appelant.
    const latest = useRef({ order, lit, typed, pick, close, menuNoResult })
    latest.current = { order, lit, typed, pick, close, menuNoResult }
    useEffect(() => {
        const doc = Platform.OS === "web" ? (globalThis as { document?: WebDocument }).document : undefined
        if (!open || doc == null) return
        const onKeyDown = (event: WebKeyEvent) => {
            const { order: list, lit: current, typed: text, menuNoResult: create } = latest.current
            const at = list.findIndex((item) => String(getItemId(item)) === String(current))
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault()
                event.stopPropagation()
                if (list.length === 0) return
                const step = event.key === "ArrowDown" ? 1 : -1
                const next = at < 0 ? 0 : Math.min(list.length - 1, Math.max(0, at + step))
                setHighlight(getItemId(list[next]))
            } else if (event.key === "Enter") {
                event.preventDefault()
                event.stopPropagation()
                if (current != null) latest.current.pick(current, true)
                else if (create != null && text.trim() !== "") create.onPress(text.trim())
            } else if (event.key === "Tab") {
                latest.current.close()
            }
        }
        // À la capture : le champ de react-native-web arrête la touche avant qu'elle ne remonte au document, et
        // la touche prise par la liste ne va pas plus loin (ni au champ, ni à l'Entrée qui génère).
        doc.addEventListener("keydown", onKeyDown, true)
        return () => doc.removeEventListener("keydown", onKeyDown, true)
        // Tout ce qui change d'un rendu à l'autre passe par `latest` : l'écouteur ne se repose qu'à l'ouverture.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    return (
        <Stack gap="1" testID={testID}>
            {label != null && (
                <Text size="text-base" weight="bold" color="primary">
                    {label}
                </Text>
            )}

            <Pressable
                // Le toucher ouvre la liste même là où le champ n'est pas à taper ; au web, le champ est la seule étape
                // du clavier.
                focusable={false}
                accessible={false}
                disabled={disabled}
                onPress={() => {
                    openList()
                    if (withSearch) inputRef.current?.focus()
                }}
            >
                <View
                    ref={boxRef}
                    collapsable={false}
                    // Ouverte d'office (une story), la liste attend la première mesure du champ.
                    onLayout={() => {
                        if (open && anchor == null) measure()
                    }}
                    // Sur un téléphone, un champ qu'on ne tape pas ne prend pas le toucher : la rangée l'ouvre.
                    pointerEvents={!withSearch && Platform.OS !== "web" ? "none" : "auto"}
                >
                    <TextInput
                        ref={inputRef}
                        value={withSearch && open ? query : (summary ?? "")}
                        onChangeText={(text) => {
                            // Fermé, le champ montre le choix en cours : ce qu'on y tape s'ajoute derrière, et seule
                            // la frappe nouvelle devient la recherche.
                            if (!open) {
                                const shown = summary ?? ""
                                openList(shown !== "" && text.startsWith(shown) ? text.slice(shown.length) : text)
                                return
                            }
                            setQuery(text)
                            setHighlight(null)
                        }}
                        placeholder={withSearch && open ? (summary ?? searchPlaceholder ?? placeholder) : placeholder}
                        onFocus={() => openList()}
                        onKeyPress={(event) => {
                            if (!open && event.nativeEvent.key === "ArrowDown") openList()
                        }}
                        editable={withSearch}
                        disabled={disabled}
                        error={error}
                        errorLive
                        left={selectedIcon != null ? <AutocompleteOptionIcon icon={selectedIcon} /> : undefined}
                        right={<Icon name={open ? "chevron-up" : "chevron-down"} size={16} color="text-muted" />}
                        accessibilityLabel={
                            label != null ? `${label} : ${summary ?? placeholder}` : (summary ?? placeholder)
                        }
                        autoCorrect={false}
                    />
                </View>
            </Pressable>

            <Overlay visible={open} onRequestClose={close} placement="anchor" anchor={anchor}>
                <Card p="1" style={styles.menu}>
                    <ScrollView
                        style={styles.scroll}
                        keyboardShouldPersistTaps="handled"
                        showsVerticalScrollIndicator
                        nestedScrollEnabled
                    >
                        <Autocomplete
                            variant="menu"
                            selection="single"
                            searchable={false}
                            query={typed}
                            items={items}
                            getItemId={getItemId}
                            getItemLabel={getItemLabel}
                            getItemIcon={getItemIcon}
                            selectedIds={selectedIds}
                            highlightedId={lit}
                            onToggle={(id) => pick(id, false)}
                            groupBy={groupBy}
                            noResultAction={menuNoResult}
                            loading={loading}
                        />
                        {!loading && order.length === 0 && (noResultAction == null || typed.trim() === "") && (
                            <Text size="text-sm" color="text-muted" style={styles.empty}>
                                Aucun résultat
                            </Text>
                        )}
                    </ScrollView>
                </Card>
            </Overlay>
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** La liste flotte sur le formulaire : la carte du livre, sa hauteur bornée par son cadre. */
    menu: { flexShrink: 1, maxHeight: "100%" as const },
    scroll: { flexGrow: 0, flexShrink: 1 },
    empty: { paddingHorizontal: theme.spacing["3"], paddingVertical: theme.spacing["2"] },
}))
